// ../utils.ts
var isBrowser = () => typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.navigator !== "undefined";
var IS_BROWSER = isBrowser();
var _a;
IS_BROWSER && // @ts-expect-error Typescript has not implemented userAgentData yet https://stackoverflow.com/a/71392474
/^Mac/i.test(((_a = navigator.userAgentData) == null ? void 0 : _a.platform) || navigator.platform);
var SUPPORTS_CONSTRUCTED_CSS = IS_BROWSER && window.CSSStyleSheet && document.adoptedStyleSheets;
var DISPLAY_BLOCK = ":host(:not([hidden])) { display: block }";
var UHTMLElement = typeof HTMLElement === "undefined" ? class {
} : HTMLElement;
var attr = (el, name, value) => {
  if (value === void 0) return el.getAttribute(name);
  if (value === null) el.removeAttribute(name);
  else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  return null;
};
var on = (el, ...rest) => {
  const [types, ...options] = rest;
  for (const type of types.split(" ")) el.addEventListener(type, ...options);
  return () => off(el, ...rest);
};
var off = (el, ...rest) => {
  const [types, ...options] = rest;
  for (const type of types.split(" ")) el.removeEventListener(type, ...options);
};
var attachStyle = (el, css) => {
  const root = el.shadowRoot || el.attachShadow({ mode: "open" });
  if (!root.querySelector("slot")) root.append(tag("slot"));
  if (!root.querySelector("style")) {
    if (!SUPPORTS_CONSTRUCTED_CSS) root.append(tag("style", null, css));
    else {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      root.adoptedStyleSheets = [sheet];
    }
  }
  return root;
};
var getRoot = (node) => {
  var _a2;
  const root = ((_a2 = node.getRootNode) == null ? void 0 : _a2.call(node)) || node.ownerDocument;
  return root instanceof Document || root instanceof ShadowRoot ? root : document;
};
var tag = (tagName, attrs, content) => {
  const el = document.createElement(tagName);
  if (content) el.textContent = content;
  if (attrs) for (const [key, val] of Object.entries(attrs)) attr(el, key, val);
  return el;
};
var customElements = {
  define: (name, instance) => !isBrowser() || window.customElements.get(name) || window.customElements.define(name, instance)
};
var declarativeShadowRoot = (style, slot = "<slot></slot>") => `<template shadowrootmode="open">${slot}<style>${style}</style></template>`;

// u-details.ts
var UHTMLDetailsStyle = `${DISPLAY_BLOCK}
::slotted([slot="summary"]) { cursor: pointer; display: block }
::slotted([slot="summary"])::before { content: ''; display: inline-block; vertical-align: middle; margin-inline: .05em .3125em; border-block: .3125em solid transparent; border-inline-start: .5em solid }
::slotted([slot="summary"][aria-expanded="true"])::before { rotate: 90deg }
:host > [part="details-content"]:not([hidden=""]) { display: block }`;
var UHTMLDetailsShadowRoot = declarativeShadowRoot(
  UHTMLDetailsStyle,
  '<slot name="summary"></slot><slot part="details-content" hidden="until-found"></slot>'
);
var UHTMLDetailsElement = class extends UHTMLElement {
  // Using underscore instead of private fields for backwards compatibility
  // Using ES2015 syntax for backwards compatibility
  static get observedAttributes() {
    return ["open"];
  }
  constructor() {
    var _a2;
    super();
    attachStyle(this, UHTMLDetailsStyle);
    (_a2 = this.shadowRoot) == null ? void 0 : _a2.prepend(
      tag("slot", { name: "summary" }),
      tag("slot", { part: "details-content" })
    );
    console.warn(
      "\x1B[1m<u-details> is deprecated as <details> now has sufficient screen reader support \u{1F389}\x1B[0m\nPlease use <details> and <summary>, but import '@u-elements/u-details/polyfill' to polyfill support for Talkback screen reader when used with Firefox on Android.",
      this
    );
  }
  connectedCallback() {
    var _a2;
    this._content = (_a2 = this.shadowRoot) == null ? void 0 : _a2.children[1];
    attr(this, "role", "group");
    on(this._content, "beforematch", this);
    on(this, "click keydown", this);
    this.attributeChangedCallback();
  }
  disconnectedCallback() {
    if (this._content) off(this._content, "beforematch", this);
    off(this, "click keydown", this);
    this._content = void 0;
  }
  attributeChangedCallback(prop, prev, next) {
    const hide = "onbeforematch" in this ? "until-found" : true;
    const open = this.open;
    for (const el of this.children)
      if (el.nodeName === "U-SUMMARY") attr(el, "aria-expanded", `${open}`);
    if (this._content) {
      attr(this._content, "aria-hidden", `${!open}`);
      attr(this._content, "tabindex", open ? null : "-1");
      this._content.hidden = open ? false : hide;
    }
    if (open && this.name) {
      const uDetailsList = getRoot(this).querySelectorAll(
        `${this.nodeName}[name="${this.name}"]`
      );
      for (const uDetails of uDetailsList)
        if (uDetails !== this) uDetails.open = false;
    }
    if (prop === "open" && prev === null !== (next === null))
      this.dispatchEvent(new Event("toggle"));
  }
  handleEvent(event) {
    var _a2, _b;
    const isKeyClick = event.key === " " || event.key === "Enter";
    const isSummary = event.target instanceof Element && ((_a2 = event.target.closest('[slot="summary"]')) == null ? void 0 : _a2.parentElement) === this;
    if (event.defaultPrevented) return;
    if (event.type === "beforematch") this.open = true;
    if (isSummary && event.type === "click") this.open = !this.open;
    if (isSummary && event.type === "keydown" && isKeyClick) {
      (_b = event.preventDefault) == null ? void 0 : _b.call(event);
      event.target.dispatchEvent(new MouseEvent("click", event));
    }
  }
  get open() {
    return this.hasAttribute("open");
  }
  set open(value) {
    attr(this, "open", value ? "" : null);
  }
  get name() {
    return attr(this, "name") || "";
  }
  set name(value) {
    attr(this, "name", value);
  }
};
var UHTMLSummaryElement = class extends UHTMLElement {
  connectedCallback() {
    attr(this, "role", "button");
    this.slot = "summary";
    this.tabIndex = 0;
  }
};
customElements.define("u-details", UHTMLDetailsElement);
customElements.define("u-summary", UHTMLSummaryElement);

export { UHTMLDetailsElement, UHTMLDetailsShadowRoot, UHTMLDetailsStyle, UHTMLSummaryElement };
